import React from "react";
import { InputNumberWithButtons } from "@jugl-web/ui-kit";
import { PendingInvoice } from "@jugl-web/rest-api";
import { useTranslations } from "@jugl-web/utils";
import { ReactComponent as MailIcon } from "./assets/mail.svg";
import { ReactComponent as SmileIcon } from "./assets/smiley.svg";
import { Addon, SelectedAddon } from "./types";
import { ReactComponent as InfoIcon } from "./assets/info.svg";
import { CreditCardBase } from "../CreditCardBase/CreditCardBase";
import { PendingInvoiceAlert } from "../PendingInvoiceAlert";

interface AddonCardProps {
  addon: Addon;
  selected?: SelectedAddon;
  onClick: () => void;
  disabled?: boolean;
  interactionDisabled?: boolean;
  pendingInvoice?: PendingInvoice;
}

const BADGE_ICON_MAP: Record<
  string,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  feature: MailIcon,
};
const DEFAULT_BADGE_ICON = SmileIcon;

export const AddonCard: React.FC<AddonCardProps> = ({
  addon,
  selected,
  onClick,
  disabled = false,
  interactionDisabled = false,
  pendingInvoice,
}) => {
  const quantity = selected?.type === "quantity" ? selected.quantity : 0;
  const badgeKey = addon.badge.trim().toLowerCase();
  const BadgeIcon = BADGE_ICON_MAP[badgeKey] ?? DEFAULT_BADGE_ICON;
  const { t } = useTranslations();

  return (
    <CreditCardBase
      icon={<BadgeIcon className="shrink-0" />}
      title={addon.name}
      description={addon.description}
      price={`$${addon.price}`}
      priceLabel={addon.unitLabel ?? ""}
      rightContent={
        disabled ? (
          <div className="flex items-center gap-3 rounded-xl bg-grey-100 p-3 font-secondary">
            <InfoIcon className="shrink-0" />
            <span className="text-sm leading-[19.6px] text-dark">
              {t({
                id: "cx_plans_page.addon_card.paid-only",
                defaultMessage: "Available only in Paid Plans",
              })}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 font-secondary text-sm text-black">
            {t({
              id: "cx_plans_page.addon_card.enabled-for",
              defaultMessage: "Enabled for",
            })}
            <div className="relative">
              {!interactionDisabled && (
                <div
                  onClick={() => {
                    onClick();
                  }}
                  className="absolute inset-0 z-[1] cursor-pointer"
                />
              )}
              <InputNumberWithButtons
                value={quantity}
                onChange={() => {}}
                className="z-0 h-8 [&>input]:w-8"
                disabled={interactionDisabled}
              />
            </div>
            {addon.id === "jugl::ai_credits"
              ? t({
                  id: "cx_plans_page.addon_card.extra-credits",
                  defaultMessage: "extra credits",
                })
              : t({
                  id: "cx_plans_page.addon_card.extra-seats",
                  defaultMessage: "extra seats",
                })}
          </div>
        )
      }
      footerAlert={
        pendingInvoice && (
          <PendingInvoiceAlert
            text={t({
              id: "cx-plans-page.addon-card.pending-invoice-alert",
              defaultMessage:
                "Payment require additional action ⚠️ Check to restore Add-on usage",
            })}
            onCompletePayment={() =>
              window.open(pendingInvoice.invoice_url, "_blank")
            }
          />
        )
      }
    />
  );
};
